import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { CALENDLY_URL, SERVICES } from "../data/content";

export const LogoMark = ({ dark = false }) => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <div className="flex flex-col justify-center gap-[3px] w-7">
      <div className={`h-[3px] w-full ${dark ? "bg-mint" : "bg-green"}`} />
      <div className={`h-[2.5px] w-[80%] ${dark ? "bg-mint" : "bg-green"}`} />
      <div className={`h-[2px] w-1/2 ${dark ? "bg-mint" : "bg-green"}`} />
    </div>
    <span className={`font-serif text-2xl tracking-tight ${dark ? "text-paper" : "text-ink"}`}>CFOSutra</span>
  </Link>
);

const NAV_LINKS = [
  { label: "Insights", to: "/#insights" },
  { label: "Resources", to: "/#resources" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/#about" },
  { label: "Contact", to: "/#contact" },
];

export default function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname, location.hash]);

  const goTo = (to) => {
    if (to.startsWith("/#")) {
      const hash = to.slice(1);
      if (location.pathname !== "/") {
        navigate("/" + hash);
      } else {
        const el = document.querySelector(hash);
        if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-paper/85 backdrop-blur-xl border-b border-rule" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <LogoMark />
        <nav className="hidden lg:flex items-center gap-8">
          <div
            className="relative"
            onMouseEnter={() => { clearTimeout(closeTimer.current); setServicesOpen(true); }}
            onMouseLeave={() => { closeTimer.current = setTimeout(() => setServicesOpen(false), 150); }}
          >
            <button
              data-testid="nav-services-dropdown"
              onClick={() => setServicesOpen(!servicesOpen)}
              className="flex items-center gap-1 text-sm font-medium text-ink-mid hover:text-green transition-colors duration-200"
            >
              Services <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[34rem]" data-testid="services-dropdown-menu">
                <div className="bg-white border border-rule rounded-sm shadow-sm grid grid-cols-2 overflow-hidden">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="p-5 hover:bg-green-lt/60 transition-colors duration-200 border-b border-r border-rule last:border-r-0"
                    >
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-green mb-2">{s.num}</div>
                      <div className="font-medium text-ink text-sm">{s.title}</div>
                      <div className="text-xs text-ink-soft mt-1 leading-relaxed">{s.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => goTo(l.to)}
              className="text-sm font-medium text-ink-mid hover:text-green transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-call-btn"
            className="inline-flex items-center gap-1.5 bg-green text-white text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-green-mid transition-colors duration-200"
          >
            Book a call <ArrowUpRight size={14} />
          </a>
        </nav>
        <button
          className="lg:hidden p-2 text-ink"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-paper border-t border-rule px-6 py-6 space-y-1" data-testid="mobile-menu">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft pb-2">Services</div>
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="block py-2 text-sm font-medium text-ink" data-testid={`mobile-service-${s.slug}`}>
              {s.title}
            </Link>
          ))}
          <div className="border-t border-rule my-3" />
          {NAV_LINKS.map((l) => (
            <button key={l.label} onClick={() => goTo(l.to)} className="block py-2 text-sm font-medium text-ink" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
              {l.label}
            </button>
          ))}
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-book-call-btn" className="mt-4 inline-flex items-center gap-1.5 bg-green text-white text-sm font-medium px-5 py-3 rounded-sm">
            Book a call <ArrowUpRight size={14} />
          </a>
        </div>
      )}
    </header>
  );
}
